import { HttpClient } from '../api/client'
import type { ApiResponse } from '@bandos/shared'
import { getAuthenticatedHeaders, transformResponse } from './aiHelpers'
import { STORYBOARD_PANEL_COUNT } from './aiTypes'
import type { StoryboardSceneBible, StoryboardCast } from '../../types/mindmap-components'

interface ImageUsageMetrics {
  inputTokens: number
  outputTokens: number
  totalTokens: number
}

interface StoryboardResult {
  storyboardUrls: string[]
  usage?: ImageUsageMetrics
}

interface StoryboardDescriptionsResult {
  storyboardTitle: string
  descriptions: string[]
  captions: string[]
  characterDescription: string
  sceneBible?: StoryboardSceneBible
  cast?: StoryboardCast
  panelSceneIndices?: number[]
  panelCharacters?: string[][]
}

/**
 * AI service for visual media generation — storyboards and storyboard descriptions.
 */
export class AIMediaService {
  /** Generate a 4-panel illustrated storyboard for an idea node */
  static async generateStoryboard(
    mindmapId: string,
    nodeId: string,
    frameDescriptions?: string[],
    sceneBible?: StoryboardSceneBible,
    cast?: StoryboardCast,
    panelSceneIndices?: number[],
    panelCharacters?: string[][]
  ): Promise<ApiResponse<StoryboardResult>> {
    const auth = await getAuthenticatedHeaders<StoryboardResult>()
    if (auth.error) return auth.error

    const requestData = {
      mindmapId,
      nodeId,
      ...(frameDescriptions &&
        frameDescriptions.length === STORYBOARD_PANEL_COUNT && { frameDescriptions }),
      ...(sceneBible && { sceneBible }),
      ...(cast && { cast }),
      ...(panelSceneIndices &&
        panelSceneIndices.length === STORYBOARD_PANEL_COUNT && { panelSceneIndices }),
      ...(panelCharacters &&
        panelCharacters.length === STORYBOARD_PANEL_COUNT && { panelCharacters }),
    }

    const response = await HttpClient.post<{
      success: boolean
      message?: string
      storyboardUrls?: string[]
      usage?: ImageUsageMetrics
    }>('/ai/generate-storyboard', requestData, auth.headers)

    return transformResponse(
      response,
      (raw) =>
        raw.success && raw.storyboardUrls
          ? { storyboardUrls: raw.storyboardUrls, usage: raw.usage }
          : null,
      'GENERATE_STORYBOARD_ERROR',
      'Failed to generate storyboard'
    )
  }

  /** Generate AI scene descriptions for storyboard panels (no images) */
  static async generateStoryboardDescriptions(
    mindmapId: string,
    nodeId: string
  ): Promise<ApiResponse<StoryboardDescriptionsResult>> {
    const auth = await getAuthenticatedHeaders<StoryboardDescriptionsResult>()
    if (auth.error) return auth.error

    const response = await HttpClient.post<{
      success: boolean
      message?: string
      storyboardTitle?: string
      descriptions?: string[]
      captions?: string[]
      characterDescription?: string
      sceneBible?: StoryboardSceneBible
      cast?: StoryboardCast
      panelSceneIndices?: number[]
      panelCharacters?: string[][]
    }>('/ai/generate-storyboard-descriptions', { mindmapId, nodeId }, auth.headers)

    return transformResponse(
      response,
      (raw) =>
        raw.success && raw.descriptions && raw.characterDescription
          ? {
              storyboardTitle: raw.storyboardTitle ?? 'Untitled Storyboard',
              descriptions: raw.descriptions,
              captions: raw.captions ?? [],
              characterDescription: raw.characterDescription,
              sceneBible: raw.sceneBible,
              cast: raw.cast,
              panelSceneIndices: raw.panelSceneIndices,
              panelCharacters: raw.panelCharacters,
            }
          : null,
      'GENERATE_STORYBOARD_DESCRIPTIONS_ERROR',
      'Failed to generate storyboard descriptions'
    )
  }
}
